import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle, ArrowRight, Crown } from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, refreshProfile } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const checkPayment = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('failed');
        return;
      }

      if (!isAuthenticated) {
        // Wait for auth to be ready
        setTimeout(checkPayment, 1000);
        return;
      }

      try {
        // Poll for payment status
        for (let i = 0; i < 10; i++) {
          const response = await subscriptionAPI.getCheckoutStatus(sessionId);
          
          if (response.data.payment_status === 'paid') {
            setPaymentDetails(response.data);
            setStatus('success');
            toast.success('Subscription activated successfully!');
            
            // Refresh user profile to get updated subscription
            if (refreshProfile) {
              await refreshProfile();
            }
            return;
          }
          
          // Wait before next poll
          await new Promise(r => setTimeout(r, 2000));
        }
        
        // If we get here, payment wasn't confirmed
        setStatus('pending');
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('failed');
      }
    };

    checkPayment();
  }, [searchParams, isAuthenticated, refreshProfile]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
        <Card className="bg-[#112240] border-[#3B82F6]/20 max-w-md w-full mx-4" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)'}}>
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-16 h-16 text-[#4ADE80] animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-[#94A3B8]">Please wait while we confirm your subscription...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
        <Card className="bg-[#112240] border-[#4ADE80]/30 max-w-md w-full mx-4" style={{boxShadow: '0 8px 32px rgba(74, 222, 128, 0.2)'}}>
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Subscription Activated!</h2>
            <p className="text-[#94A3B8] mb-6">
              Welcome to HaulLogix! Your subscription is now active.
            </p>
            
            <div className="bg-[#0D1B2A] rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-[#4ADE80]" />
                <span className="text-white font-semibold">Your Benefits</span>
              </div>
              <ul className="text-sm text-[#94A3B8] space-y-1">
                <li>Unlimited load access</li>
                <li>Priority support</li>
                <li>Advanced features unlocked</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black hover:from-[#86EFAC] hover:to-[#4ADE80] font-semibold"
                style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/loadboard">
                <Button variant="outline" className="w-full border-[#3B82F6]/30 text-white">
                  Browse Loads
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
        <Card className="bg-[#112240] border-[#F59E0B]/30 max-w-md w-full mx-4" style={{boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)'}}>
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-[#F59E0B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-[#F59E0B]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Processing</h2>
            <p className="text-[#94A3B8] mb-6">
              Your payment is still being processed. This may take a few moments.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black"
            >
              Check Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed status
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
      <Card className="bg-[#112240] border-[#EF4444]/30 max-w-md w-full mx-4" style={{boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)'}}>
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-20 h-20 bg-[#EF4444]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-[#EF4444]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
          <p className="text-[#94A3B8] mb-6">
            There was an issue processing your payment. Please try again or contact support.
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/pricing">
              <Button className="w-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black">
                Try Again
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full border-[#3B82F6]/30 text-white">
                Contact Support
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
